/** LibraryTab — manufacturer catalog + Equipment List + Selected Parts. */
import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, Upload, Download, CopySimple } from "@phosphor-icons/react";
import { nextTagFor, downloadFile } from "./_shared";
import ContextMenu from "../../components/ContextMenu";

export default function LibraryTab({ project, update, projectId }) {
  const [items, setItems] = useState([]);
  const [mfg, setMfg] = useState("all");
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [mfgs, setMfgs] = useState([]);
  const csvRef = useRef();

  useEffect(() => {
    api.get("/manufacturers").then(({ data }) => setMfgs(data)).catch(() => {});
  }, []);
  useEffect(() => {
    const params = {};
    if (mfg !== "all") params.manufacturer = mfg;
    if (cat !== "all") params.category = cat;
    if (q) params.q = q;
    api.get("/equipment", { params }).then(({ data }) => setItems(data)).catch(() => {});
  }, [mfg, cat, q]);

  const equipment = project.equipment || [];
  const loadList = project.equipment_list || [];
  const mmToIn = (mm) => Math.round(((mm || 0) / 25.4) * 10) / 10;

  const add = (eq) => {
    const idx = equipment.findIndex((x) => x.part_number === eq.part_number && x.manufacturer === eq.manufacturer);
    let nextEquip;
    if (idx >= 0) {
      nextEquip = equipment.map((x, i) => (i === idx ? { ...x, quantity: x.quantity + 1 } : x));
    } else {
      nextEquip = [...equipment, {
        part_number: eq.part_number, manufacturer: eq.manufacturer, category: eq.category,
        description: eq.description, quantity: 1, unit_price: eq.price,
      }];
    }
    const eqType = `${eq.category} — ${eq.part_number}`;
    const suggested = nextTagFor(eq.category || eq.part_number, loadList);
    const tagIn = (typeof window !== "undefined" && window.prompt)
      ? window.prompt(`Tag for "${eq.part_number}" (Enter to accept default):`, suggested)
      : suggested;
    if (tagIn === null) return;
    const finalTag = (tagIn || "").trim() || suggested;
    const llNew = {
      id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()),
      tag: finalTag, equipment_type: eqType,
      weight_lbs: eq.weight_lbs || 0,
      length_in: mmToIn(eq.length_mm), width_in: mmToIn(eq.width_mm), depth_in: mmToIn(eq.depth_mm),
      special_considerations: "", source: "library",
      part_number: eq.part_number, manufacturer: eq.manufacturer,
    };
    update({ equipment: nextEquip, equipment_list: [...loadList, llNew] });
    toast.success(`Added ${eq.part_number} — also added to Equipment List`);
  };

  const del = (i) => update({ equipment: equipment.filter((_, idx) => idx !== i) });
  const setQty = (i, qty) => {
    const next = [...equipment];
    next[i] = { ...next[i], quantity: Math.max(1, qty) };
    update({ equipment: next });
  };

  const llAdd = () => {
    const typeIn = (typeof window !== "undefined" && window.prompt)
      ? window.prompt("Equipment type (e.g. VFD, Motor, Transformer, Pump, MCC):", "")
      : "";
    if (typeIn === null) return;
    const eqType = (typeIn || "").trim() || "Equipment";
    const suggested = nextTagFor(eqType, loadList);
    const tagIn = (typeof window !== "undefined" && window.prompt)
      ? window.prompt(`Tag for this item (Enter for "${suggested}"):`, suggested)
      : suggested;
    if (tagIn === null) return;
    const finalTag = (tagIn || "").trim() || suggested;
    update({
      equipment_list: [...loadList, {
        id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()),
        tag: finalTag, equipment_type: eqType, weight_lbs: 0, length_in: 0, width_in: 0, depth_in: 0,
        special_considerations: "", source: "manual",
      }],
    });
  };
  const llEdit = (i, patch) => {
    const next = [...loadList];
    next[i] = { ...next[i], ...patch };
    update({ equipment_list: next });
  };
  const llDel = (i) => update({ equipment_list: loadList.filter((_, idx) => idx !== i) });

  const [ctxMenu, setCtxMenu] = useState(null);
  // Duplicate a equipment-list row through the backend so the clone gets a server-
  // generated id + ' (copy)' tag + 12-in x offset on any positioned equipment.
  const llDuplicate = async (item) => {
    try {
      const { data } = await api.post(`/projects/${projectId}/equipment-list/${item.id}/duplicate`);
      update({ equipment_list: [...loadList, data] });
      toast.success(`Duplicated ${data.tag}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Duplicate failed");
    }
  };

  const llImport = async (file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const { data: r } = await api.post(`/projects/${projectId}/equipment-list/csv-import`, form);
      const { data: fresh } = await api.get(`/projects/${projectId}`);
      update({ equipment_list: fresh.equipment_list });
      toast.success(`Imported ${r.imported} equipment-list rows`);
    } catch {
      toast.error("Equipment list CSV import failed");
    }
  };
  const llExport = async () => {
    try {
      const resp = await fetch(`${API}/projects/${projectId}/equipment-list/csv-export`, { credentials: "include" });
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${project.name.replace(/[^A-Za-z0-9_-]+/g, "_")}_equipment_list.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Equipment list CSV exported");
    } catch { toast.error("Export failed"); }
  };
  const llTemplate = async () => {
    try {
      const resp = await fetch(`${API}/projects/${projectId}/equipment-list/csv-template`, { credentials: "include" });
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "equipment_list_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Template downloaded — open in Excel/Sheets and read the # lines for instructions");
    } catch { toast.error("Template download failed"); }
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* LIBRARY */}
        <div>
          <div className="label-tag mb-2">// Browse catalog</div>
          <h2 className="font-display text-2xl font-bold">Manufacturer library</h2>
          <div className="grid grid-cols-3 gap-2 mt-4">
            <select value={mfg} onChange={(e) => setMfg(e.target.value)}
              className="bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none" data-testid="lib-mfg">
              <option value="all">All mfg</option>
              {mfgs.map((m) => <option key={m.slug} value={m.slug}>{m.name}</option>)}
            </select>
            <select value={cat} onChange={(e) => setCat(e.target.value)}
              className="bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none" data-testid="lib-cat">
              <option value="all">All categories</option>
              <option value="breaker">Breaker</option><option value="contactor">Contactor</option>
              <option value="vfd">VFD</option><option value="transformer">Transformer</option>
              <option value="enclosure">Enclosure</option><option value="relay">Relay</option>
              <option value="plc">PLC</option><option value="panelboard">Panelboard</option>
              <option value="heat_trace_controller">Heat Trace Ctrl</option>
            </select>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search…"
              className="bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none" data-testid="lib-search" />
          </div>

          <div className="mt-2 flex items-center gap-2 text-xs font-mono">
            <button
              onClick={async () => {
                if (!q) { toast.info("Type a part number first"); return; }
                try {
                  const { data } = await api.get("/integrations/idea/search", { params: { q } });
                  if (data && data.length) { setItems(data); toast.success(`IDEA Connector: ${data.length} result(s)`); }
                  else toast.info("IDEA returned no matches.");
                } catch (e) {
                  toast.warning(e.response?.data?.detail || "IDEA Connector not available");
                }
              }}
              className="btn-ghost py-1 px-3" data-testid="lib-search-idea" title="Search the IDEA Industry Data Warehouse">
              Search via IDEA Connector
            </button>
            <span className="text-gray-500">Real-time product data from manufacturers via IDEA IDW.</span>
          </div>

          <div className="mt-4 border border-white/10 max-h-[60vh] overflow-y-auto">
            {items.map((e) => (
              <div key={`${e.manufacturer}-${e.part_number}`} className="border-b border-white/5 p-3 hover:bg-white/[0.03] flex items-start justify-between gap-3" data-testid={`lib-item-${e.part_number}`}>
                <div className="font-mono text-sm">
                  <div className="text-[#00E5FF]">{e.part_number} <span className="text-gray-500 capitalize">· {e.manufacturer}</span></div>
                  <div className="text-gray-300 text-xs mt-1">{e.description}</div>
                  <div className="text-gray-500 text-xs mt-1">
                    {e.amps ? `${e.amps}A · ` : ""}{e.poles ? `${e.poles}P · ` : ""}{e.voltage ? `${e.voltage}V` : ""}
                    {e.weight_lbs ? ` · ${e.weight_lbs} lbs` : ""}
                    {e.length_mm ? ` · ${mmToIn(e.length_mm)}×${mmToIn(e.width_mm)}×${mmToIn(e.depth_mm)} in` : ""}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="font-mono text-sm">${e.price.toFixed(2)}</div>
                  <button onClick={() => add(e)} className="btn-primary text-xs py-1 px-3 inline-flex items-center gap-1" data-testid={`add-${e.part_number}`}>
                    <Plus size={12} weight="bold" /> Add
                  </button>
                </div>
              </div>
            ))}
            {items.length === 0 && <div className="p-6 text-center text-gray-500 text-sm">No parts found</div>}
          </div>
        </div>

        {/* LOAD LIST */}
        <div>
          <div className="flex items-end justify-between flex-wrap gap-2 mb-2">
            <div>
              <div className="label-tag">// Equipment List</div>
              <h2 className="font-display text-2xl font-bold">Equipment list ({loadList.length})</h2>
            </div>
            <div className="flex gap-2 flex-wrap">
              <input ref={csvRef} type="file" accept=".csv" className="hidden"
                onChange={(e) => e.target.files?.[0] && llImport(e.target.files[0])}
                data-testid="loadlist-import-input" />
              <button onClick={llTemplate} className="btn-ghost text-xs py-1.5 inline-flex items-center gap-1" data-testid="loadlist-template" title="Download blank CSV with instructions">
                <Download size={14} /> Template
              </button>
              <button onClick={() => csvRef.current?.click()} className="btn-ghost text-xs py-1.5 inline-flex items-center gap-1" data-testid="loadlist-import">
                <Upload size={14} /> Import
              </button>
              <button onClick={llExport} className="btn-ghost text-xs py-1.5 inline-flex items-center gap-1" data-testid="loadlist-export">
                <Download size={14} /> Export
              </button>
              <button
                onClick={async () => {
                  try {
                    await downloadFile(`/projects/${projectId}/equipment-list/xlsx`, `${(project.name || "project").replace(/[^A-Za-z0-9_-]+/g, "_")}_equipment_list.xlsx`);
                    toast.success("XLSX exported");
                  } catch { toast.error("XLSX export failed"); }
                }}
                className="btn-ghost text-xs py-1.5 inline-flex items-center gap-1" data-testid="loadlist-export-xlsx" disabled={loadList.length === 0}>
                <Download size={14} /> XLSX
              </button>
              <button onClick={llAdd} className="btn-primary text-xs py-1.5 inline-flex items-center gap-1" data-testid="loadlist-add">
                <Plus size={14} weight="bold" /> Add
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-400 mb-2">
            Physical equipment with tags + dimensions. Click <b>Template</b> for a CSV with instructions.
          </p>

          <div className="border border-white/10 overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full" data-testid="loadlist-table">
              <thead className="bg-[#1A1F29] sticky top-0">
                <tr className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-mono text-left">
                  <th className="p-2">#</th><th className="p-2">Tag</th><th className="p-2">Type</th>
                  <th className="p-2">Wt</th><th className="p-2">L</th><th className="p-2">W</th><th className="p-2">D</th>
                  <th className="p-2" title="Mounting bracket / stand-off depth — gap between equipment back face and the surface it's attached to (wall OR floor). 0 = mounted directly; > 0 = bracket-mounted.">Mnt&nbsp;depth</th>
                  <th className="p-2" title="Where the equipment is installed relative to the enclosure. Interior = inside the room. Exterior = on the OUTSIDE face of the closest wall (lights, receptacles, HVAC condenser). Through = straddles the wall (HVAC pass-through, through-wall PT/CT bay).">Install</th>
                  <th className="p-2">Notes</th>
                  <th className="p-2" title="Equipment manufacturer (e.g. ABB, Eaton). Flows into every section BOM (v2.26).">Manufacturer</th>
                  <th className="p-2" title="Manufacturer part / catalog number. Flows into every section BOM.">Part&nbsp;#</th>
                  <th className="p-2" title="Who supplies the equipment? Owner / Contractor / Vendor (free-text).">Supplied&nbsp;by</th>
                  <th className="p-2" title="Who installs the equipment? Owner / Contractor / Vendor (free-text).">Installed&nbsp;by</th>
                  <th className="p-2" title="Free-text comment that appears on every section BOM next to this equipment.">Comments</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {loadList.map((row, i) => (
                  <tr key={row.id || i} className="border-t border-white/5 font-mono text-sm"
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setCtxMenu({
                          x: e.clientX, y: e.clientY,
                          items: [
                            { label: "Duplicate", icon: <CopySimple size={12} />, hint: "+12 in",
                              disabled: !row.id,
                              onClick: () => llDuplicate(row) },
                            { label: "Delete", danger: true, icon: <Trash size={12} />,
                              onClick: () => llDel(i) },
                          ],
                        });
                      }}
                      data-testid={`loadlist-row-${i}`}>
                    <td className="p-2 text-gray-500">{String(i + 1).padStart(2, "0")}</td>
                    <td className="p-2"><input value={row.tag} onChange={(e) => llEdit(i, { tag: e.target.value })} placeholder="PUMP-A" className="bg-black/40 border border-white/10 px-2 py-1 w-20 focus:border-[#00E5FF] outline-none" /></td>
                    <td className="p-2"><input value={row.equipment_type} onChange={(e) => llEdit(i, { equipment_type: e.target.value })} placeholder="Pump" className="bg-black/40 border border-white/10 px-2 py-1 w-32 focus:border-[#00E5FF] outline-none" /></td>
                    <td className="p-2"><input type="number" value={row.weight_lbs || ""} placeholder="0" onChange={(e) => llEdit(i, { weight_lbs: e.target.value === "" ? 0 : Number(e.target.value) })} className="bg-black/40 border border-white/10 px-2 py-1 w-16 focus:border-[#00E5FF] outline-none" /></td>
                    <td className="p-2"><input type="number" value={row.length_in || ""} placeholder="0" onChange={(e) => llEdit(i, { length_in: e.target.value === "" ? 0 : Number(e.target.value) })} className="bg-black/40 border border-white/10 px-2 py-1 w-14 focus:border-[#00E5FF] outline-none" /></td>
                    <td className="p-2"><input type="number" value={row.width_in || ""} placeholder="0" onChange={(e) => llEdit(i, { width_in: e.target.value === "" ? 0 : Number(e.target.value) })} className="bg-black/40 border border-white/10 px-2 py-1 w-14 focus:border-[#00E5FF] outline-none" /></td>
                    <td className="p-2"><input type="number" value={row.depth_in || ""} placeholder="0" onChange={(e) => llEdit(i, { depth_in: e.target.value === "" ? 0 : Number(e.target.value) })} className="bg-black/40 border border-white/10 px-2 py-1 w-14 focus:border-[#00E5FF] outline-none" /></td>
                    <td className="p-2"><input type="number" min={0} value={row.mounting_depth_in || ""} placeholder="0" title="Mounting bracket / stand-off depth in inches. 0 = direct mount against wall/floor. > 0 = bracket-mounted (equipment stands off the surface by this distance)." onChange={(e) => llEdit(i, { mounting_depth_in: e.target.value === "" ? 0 : Number(e.target.value) })} className="bg-black/40 border border-white/10 px-2 py-1 w-16 focus:border-[#FFCC00] outline-none" data-testid={`loadlist-mntdepth-${i}`} /></td>
                    <td className="p-2">
                      <select value={row.installation_side || "interior"}
                              onChange={(e) => llEdit(i, { installation_side: e.target.value })}
                              className="bg-black/40 border border-white/10 px-2 py-1 focus:border-[#00E5FF] outline-none"
                              data-testid={`loadlist-install-${i}`}>
                        <option value="interior">Interior</option>
                        <option value="exterior">Exterior</option>
                        <option value="through">Through</option>
                      </select>
                    </td>
                    <td className="p-2"><input value={row.special_considerations} onChange={(e) => llEdit(i, { special_considerations: e.target.value })} placeholder="Wet, hazardous…" className="bg-black/40 border border-white/10 px-2 py-1 w-full min-w-[120px] focus:border-[#00E5FF] outline-none" /></td>
                    {/* v2.26 — BOM columns that flow into every section deliverable */}
                    <td className="p-2"><input value={row.manufacturer || ""} onChange={(e) => llEdit(i, { manufacturer: e.target.value })} placeholder="ABB" className="bg-black/40 border border-white/10 px-2 py-1 w-24 focus:border-[#FF9F0A] outline-none" data-testid={`loadlist-mfg-${i}`} /></td>
                    <td className="p-2"><input value={row.part_number || ""} onChange={(e) => llEdit(i, { part_number: e.target.value })} placeholder="MNS-3000" className="bg-black/40 border border-white/10 px-2 py-1 w-28 focus:border-[#FF9F0A] outline-none" data-testid={`loadlist-partnum-${i}`} /></td>
                    <td className="p-2"><input value={row.supplied_by || ""} onChange={(e) => llEdit(i, { supplied_by: e.target.value })} placeholder="Owner" list={`supplied-options`} className="bg-black/40 border border-white/10 px-2 py-1 w-24 focus:border-[#FF9F0A] outline-none" data-testid={`loadlist-supplied-${i}`} /></td>
                    <td className="p-2"><input value={row.installed_by || ""} onChange={(e) => llEdit(i, { installed_by: e.target.value })} placeholder="Contractor" list={`installed-options`} className="bg-black/40 border border-white/10 px-2 py-1 w-28 focus:border-[#FF9F0A] outline-none" data-testid={`loadlist-installed-${i}`} /></td>
                    <td className="p-2"><input value={row.comments || ""} onChange={(e) => llEdit(i, { comments: e.target.value })} placeholder="BOM comment" className="bg-black/40 border border-white/10 px-2 py-1 w-full min-w-[140px] focus:border-[#FF9F0A] outline-none" data-testid={`loadlist-comments-${i}`} /></td>
                    <td className="p-2 text-right">
                      <button onClick={() => llDel(i)} className="text-gray-500 hover:text-red-400" data-testid={`loadlist-del-${i}`}><Trash size={16} /></button>
                    </td>
                  </tr>
                ))}
                {loadList.length === 0 && (
                  <tr><td colSpan={16} className="p-8 text-center text-gray-500 text-sm">
                    No equipment-list items yet — import CSV, add manually, or pick from the library on the left.
                  </td></tr>
                )}
              </tbody>
            </table>
            {/* v2.26 — shared datalists for Supplied By / Installed By autocomplete */}
            <datalist id="supplied-options">
              <option value="Owner" />
              <option value="Contractor" />
              <option value="Vendor" />
              <option value="By Others" />
            </datalist>
            <datalist id="installed-options">
              <option value="Owner" />
              <option value="Contractor" />
              <option value="Vendor" />
              <option value="By Others" />
            </datalist>
          </div>
        </div>
      </div>

      {/* SELECTED PARTS */}
      <div>
        <div className="label-tag mb-2">// Project equipment · catalog parts (BOM)</div>
        <h2 className="font-display text-2xl font-bold">Selected parts ({equipment.length})</h2>
        <p className="text-sm text-gray-400 mt-1 mb-3">Catalog items that roll up to the project BOM together with the Equipment List.</p>
        <div className="border border-white/10">
          {equipment.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">No parts selected — pick from the catalog above.</div>
          ) : equipment.map((e, i) => (
            <div key={`${e.manufacturer}-${e.part_number}-${i}`} className="border-b border-white/5 p-3 flex items-center justify-between font-mono text-sm">
              <div>
                <div className="text-[#00E5FF]">{e.part_number} <span className="text-gray-500 capitalize">· {e.manufacturer}</span></div>
                <div className="text-gray-400 text-xs">{e.description}</div>
              </div>
              <div className="flex items-center gap-3">
                <input type="number" min={1} value={e.quantity} onChange={(ev) => setQty(i, Number(ev.target.value))}
                  className="bg-black/40 border border-white/10 px-2 py-1 w-16 text-center focus:border-[#00E5FF] outline-none" data-testid={`qty-${e.part_number}`} />
                <span className="text-gray-400 w-20 text-right">${(e.unit_price * e.quantity).toFixed(2)}</span>
                <button onClick={() => del(i)} className="text-gray-500 hover:text-red-400" data-testid={`remove-${e.part_number}`}><Trash size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <ContextMenu menu={ctxMenu} onClose={() => setCtxMenu(null)} />
    </div>
  );
}
